import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Property } from '../../../../types/property';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatPrice } from '../../../../utils/formatters';

interface OverviewChartsProps {
  properties: Property[];
  dateRange: [Date, Date];
}

const COLORS = ['#0a8345', '#4ade80', '#86efac', '#bbf7d0'];

export const OverviewCharts: React.FC<OverviewChartsProps> = ({ properties, dateRange }) => {
  // Données pour le camembert des statuts
  const statusData = properties.reduce((acc, property) => {
    const status = property.status;
    const label = {
      'for-sale': 'En vente',
      'for-rent': 'En location',
      'sold': 'Vendus',
      'rented': 'Loués',
      'archived': 'Archivés'
    }[status] || status;
    
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pieData = Object.entries(statusData).map(([status, value]) => ({
    name: {
      'for-sale': 'En vente',
      'for-rent': 'En location',
      'sold': 'Vendus',
      'rented': 'Loués',
      'archived': 'Archivés'
    }[status] || status,
    value
  }));

  // Données pour l'histogramme des transactions par mois
  const monthlyData = properties.reduce((acc, property) => {
    const month = format(property.createdAt, 'MMMM yyyy', { locale: fr });
    
    if (!acc[month]) {
      acc[month] = {
        month,
        ventes: 0,
        locations: 0,
        commissions: 0,
      };
    }

    if (property.status === 'sold') {
      acc[month].ventes += property.price;
      acc[month].commissions += property.commission;
    } else if (property.status === 'rented') {
      acc[month].locations += property.price;
      acc[month].commissions += property.commission;
    }

    return acc;
  }, {} as Record<string, any>);

  const barData = Object.values(monthlyData);

  // Données pour le graphique linéaire des délais moyens
  const timelineData = properties
    .filter(p => p.status === 'sold' || p.status === 'rented')
    .reduce((acc, property) => {
      const month = format(property.createdAt, 'MMMM yyyy', { locale: fr });
      const days = Math.round((property.updatedAt.getTime() - property.createdAt.getTime()) / (1000 * 60 * 60 * 24));
      
      if (!acc[month]) {
        acc[month] = {
          month,
          'Délai vente': 0,
          'Délai location': 0,
          countSale: 0,
          countRent: 0,
        };
      }

      if (property.status === 'sold') {
        acc[month]['Délai vente'] += days;
        acc[month].countSale++;
      } else if (property.status === 'rented') {
        acc[month]['Délai location'] += days;
        acc[month].countRent++;
      }

      return acc;
    }, {} as Record<string, any>);

  const lineData = Object.entries(timelineData).map(([month, data]) => ({
    month,
    'Délai vente': data.countSale > 0 ? Math.round(data['Délai vente'] / data.countSale) : 0,
    'Délai location': data.countRent > 0 ? Math.round(data['Délai location'] / data.countRent) : 0,
  }));

  return (
    <div className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        {/* Camembert des statuts */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Répartition des biens
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Histogramme des transactions */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            Volume des transactions
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: number) => Math.floor(value).toLocaleString('fr-FR').replace(/,/g, ' ') + ' DA'} />
                <Legend />
                <Bar name="Ventes" dataKey="ventes" fill="#0a8345" />
                <Bar name="Locations" dataKey="locations" fill="#4ade80" />
                <Bar name="Commissions" dataKey="commissions" fill="#86efac" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Graphique linéaire des délais */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
          Évolution des délais moyens (en jours)
        </h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Délai vente" stroke="#0a8345" />
              <Line type="monotone" dataKey="Délai location" stroke="#4ade80" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};